/**
 * Pagos y devoluciones: una línea por cada vez que entró o volvió dinero.
 *
 * POR QUÉ EXISTE
 * --------------
 * Myriam, 31-oct-2026: "Yo en Xero reviso cuando se cobro si luego se devolvio
 * y si se volvio a cobrar, y eso lo uso para contestar a los clientes".
 *
 * Va en la ficha del cliente, con todas sus facturas, porque cuando alguien
 * llama diciendo que ya pagó, soporte empieza por el cliente. Y en el detalle
 * de una factura, solo con las suyas.
 *
 * Las cuentas están en `lib/historialCobros.js`; aquí solo se pinta.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ORIGENES } from '@/lib/historialCobros';

const euros = (n) => `${(Number(n) || 0).toLocaleString('es-ES', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} €`;

/** "5 oct 2026". Sin `date-fns/locale`: Jest no sabe leerlo y esto no lo necesita. */
function fecha(valor) {
  const d = new Date(valor || '');
  if (!valor || Number.isNaN(d.getTime())) return 'Fecha no consta';
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

const TIPOS = {
  cobro: { label: 'Cobro', class: 'bg-green-100 text-green-800' },
  abono_saldado: { label: 'Abono saldado', class: 'bg-blue-100 text-blue-800' },
  devolucion: { label: 'Recibo devuelto', class: 'bg-amber-100 text-amber-900' },
};

/** De dónde salió, o por qué volvió: lo que se le contesta al cliente. */
function detalle(m) {
  if (m.tipo === 'devolucion') {
    const partes = [];
    if (m.motivo) partes.push(m.motivo);
    // Sin decir CUÁNDO pasó: casi todas son de antes del #16, pero también
    // puede ser de hoy si el cobro que volvió no estaba apuntado.
    if (m.anteriorALaUltima) {
      partes.push(m.veces > 1
        ? `${m.veces} anteriores a la última: no consta su fecha`
        : 'Anterior a la última: no consta su fecha');
    } else if (m.sinCobroApuntado) {
      partes.push(m.veces > 1
        ? `${m.veces} devoluciones sin su cobro apuntado: no consta su importe`
        : 'El cobro devuelto no estaba apuntado: no consta su importe');
    }
    return partes.join(' · ');
  }
  return [ORIGENES[m.origen] || m.origen, m.nota].filter(Boolean).join(' · ');
}

/**
 * @param movimientos  lo que devuelve `movimientosDeCobro`
 * @param conFactura   enseñar el número de factura (en la ficha del cliente)
 * @param onAbrirFactura  al pulsar una línea, si se quiere abrir su factura
 */
export function HistorialCobros({ movimientos, conFactura = false, onAbrirFactura }) {
  if (!movimientos || movimientos.length === 0) {
    return (
      <p className="text-sm text-gray-500" data-testid="historial-vacio">
        No consta ningún cobro ni devolución.
      </p>
    );
  }

  return (
    <ul className="divide-y" data-testid="historial-cobros">
      {movimientos.map((m) => {
        const tipo = TIPOS[m.tipo] || TIPOS.cobro;
        const pulsable = Boolean(onAbrirFactura);
        return (
          <li
            key={m.clave}
            onClick={pulsable ? () => onAbrirFactura(m.factura) : undefined}
            className={`py-3 text-sm ${pulsable ? 'cursor-pointer hover:bg-gray-50' : ''}`}
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="w-24 shrink-0 text-gray-600">{fecha(m.fecha)}</span>
              {conFactura && (
                <span className="font-mono font-semibold text-blue-600">
                  {m.factura.invoice_number}
                </span>
              )}
              <Badge className={tipo.class}>{tipo.label}</Badge>
              <span className="ml-auto font-semibold">
                {m.importe === null ? '—' : euros(m.importe)}
              </span>
            </div>
            {detalle(m) && (
              <p className="mt-1 text-xs text-gray-500 sm:pl-[6.75rem]">{detalle(m)}</p>
            )}
            {m.recuperacionEnPayGold && (
              <p className="mt-1 flex items-center gap-1 text-xs text-amber-800 sm:pl-[6.75rem]">
                <AlertTriangle className="h-3 w-3" />
                La recuperación la lleva PayGold, y si ya se ha cobrado allí Osagest
                todavía no lo sabe: compruébalo en PayGold.
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default HistorialCobros;
